import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const INIT = { name: '', email: '', phone: '', address: '', city: '', country: '', status: 'active' };
const LIMIT = 15;

export default function Customers({ setTitle }) {
  const [items, setItems]     = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [search, setSearch]   = useState('');
  const [loading, setLoading] = useState(false);
  const [modal, setModal]     = useState(false);
  const [form, setForm]       = useState(INIT);
  const [editId, setEditId]   = useState(null);
  const [saving, setSaving]   = useState(false);

  useEffect(() => { setTitle('Customers'); }, [setTitle]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/customers?search=${encodeURIComponent(search)}&page=${page}&limit=${LIMIT}`);
      setItems(data.data || []); setTotal(data.total || 0);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [search, page]);

  useEffect(() => { load(); }, [load]);

  const openAdd  = () => { setForm(INIT); setEditId(null); setModal(true); };
  const openEdit = (item) => { setForm({ ...item }); setEditId(item.id); setModal(true); };
  const closeModal = () => setModal(false);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) return alert('Customer name is required');
    setSaving(true);
    try {
      if (editId) await api.put(`/customers/${editId}`, form);
      else        await api.post('/customers', form);
      closeModal(); load();
    } catch (err) {
      alert(err.response?.data?.error || 'Error saving customer');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try { await api.delete(`/customers/${id}`); load(); }
    catch (err) { alert(err.response?.data?.error || 'Error deleting'); }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="page">
      <div className="page-header">
        <h1>Customers</h1>
        <button className="btn btn-primary" onClick={openAdd}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Add Customer
        </button>
      </div>

      <div className="card">
        <div className="toolbar">
          <div className="search-box">
            <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <input placeholder="Search by name, email, phone…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
          </div>
          <span className="text-muted" style={{ fontSize: 12, marginLeft: 'auto' }}>{total} customers</span>
        </div>

        <div className="table-wrap">
          {loading ? <div className="loading">Loading…</div> : (
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>City</th>
                  <th>Country</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={8} className="empty">No customers found</td></tr>
                ) : items.map((item, i) => (
                  <tr key={item.id}>
                    <td className="text-muted">{(page - 1) * LIMIT + i + 1}</td>
                    <td><strong>{item.name}</strong></td>
                    <td className="text-muted">{item.email || '—'}</td>
                    <td>{item.phone || '—'}</td>
                    <td>{item.city || '—'}</td>
                    <td>{item.country || '—'}</td>
                    <td><span className={`badge badge-${item.status}`}>{item.status}</span></td>
                    <td>
                      <div className="actions">
                        <button className="btn-icon" title="Edit" onClick={() => openEdit(item)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                        <button className="btn-icon danger" title="Delete" onClick={() => handleDelete(item.id, item.name)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6M9 6V4h6v2"/></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <span>Showing {Math.min((page - 1) * LIMIT + 1, total)}–{Math.min(page * LIMIT, total)} of {total}</span>
            <div className="pagination-btns">
              <button className="page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button key={p} className={`page-btn ${p === page ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button className="page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
            </div>
          </div>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="modal">
            <div className="modal-header">
              <h2>{editId ? 'Edit Customer' : 'Add Customer'}</h2>
              <button className="modal-close" onClick={closeModal}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group full">
                  <label>Full Name *</label>
                  <input className="form-control" value={form.name} onChange={e => f('name', e.target.value)} placeholder="Customer name" />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" className="form-control" value={form.email} onChange={e => f('email', e.target.value)} placeholder="email@example.com" />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input className="form-control" value={form.phone} onChange={e => f('phone', e.target.value)} placeholder="+1 234 567 8900" />
                </div>
                <div className="form-group full">
                  <label>Address</label>
                  <input className="form-control" value={form.address} onChange={e => f('address', e.target.value)} placeholder="Street address" />
                </div>
                <div className="form-group">
                  <label>City</label>
                  <input className="form-control" value={form.city} onChange={e => f('city', e.target.value)} placeholder="City" />
                </div>
                <div className="form-group">
                  <label>Country</label>
                  <input className="form-control" value={form.country} onChange={e => f('country', e.target.value)} placeholder="Country" />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select className="form-control" value={form.status} onChange={e => f('status', e.target.value)}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : editId ? 'Update Customer' : 'Create Customer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
